import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import {Formik} from 'formik'
import {Button} from '../../buttons/button'

const StyledDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`

const StyledButton = styled(Button)`
  &&& {
    min-width: initial;
  }
`

function getRange(start, end) {
  return Array(end - start + 1)
    .fill()
    .map((v, i) => i + start)
}

/*
 * https://gist.github.com/kottenator/9d936eb3e4e3c3e02598?permalink_comment_id=3238804#gistcomment-3238804
 */
function generateVisiblePages(currentPage, pageCount, maxNum) {
  if (!pageCount) {
    return []
  }

  let delta
  if (pageCount <= maxNum) {
    // delta === 7: [1 2 3 4 5 6 7]
    delta = maxNum
  } else {
    // delta === 2: [1 ... 4 5 6 ... 10]
    // delta === 4: [1 2 3 4 5 ... 10]
    delta = currentPage > 4 && currentPage < pageCount - 3 ? 2 : 4
  }

  const range = {
    start: Math.round(currentPage - delta / 2),
    end: Math.round(currentPage + delta / 2)
  }

  if (range.start - 1 === 1 || range.end + 1 === pageCount) {
    range.start += 1
    range.end += 1
  }

  let pages = currentPage > delta ? getRange(Math.min(range.start, pageCount - delta), Math.min(range.end, pageCount)) : getRange(1, Math.min(pageCount, delta + 1))

  const withDots = (value, pair) => (pages.length + 1 !== pageCount ? pair : [value])

  if (pages[0] !== 1) {
    pages = withDots(1, [1, '...']).concat(pages)
  }

  if (pages[pages.length - 1] < pageCount) {
    pages = pages.concat(withDots(pageCount, ['...', pageCount]))
  }

  return pages
}

const DataTablePagination = ({
  handleSubmit = (values, actions) => {
    actions.setSubmitting(false)
  },
  pages,
  page = 1,
  maxNumPageButtons = 6,
  ...otherProps
}) => {
  return (
    <Formik
      initialValues={{page}}
      enableReinitialize={true}
      validate={() => {
        const errors = {}
        return errors
      }}
      onSubmit={(values, actions) => handleSubmit(values, actions)}>
      {({values, isSubmitting, submitForm, handleSubmit, handleReset, setFieldValue}) => (
        <form onReset={handleReset} onSubmit={handleSubmit}>
          <StyledDiv {...otherProps}>
            <Button
              type="submit"
              label="Prev"
              icon="keyboard_arrow_left"
              onClick={() => {
                setFieldValue('page', values.page - 1)
                submitForm()
              }}
              disabled={values.page <= 1 || isSubmitting}
            />
            {(() => {
              const visiblePages = generateVisiblePages(values.page, pages, maxNumPageButtons)
              return visiblePages.map((p, index) => (
                <StyledButton
                  type="submit"
                  label={p}
                  key={index}
                  onClick={() => {
                    setFieldValue('page', p)
                    submitForm()
                  }}
                  disabled={p === '...'}
                  unelevated={values.page === p}
                  theme={values.page === p ? ['neutral3Bg', 'onNeutral3'] : undefined}
                />
              ))
            })()}
            <Button
              type="submit"
              label="Next"
              trailingIcon="keyboard_arrow_right"
              onClick={() => {
                setFieldValue('page', values.page + 1)
                submitForm()
              }}
              disabled={values.page >= pages || isSubmitting}
            />
          </StyledDiv>
        </form>
      )}
    </Formik>
  )
}

DataTablePagination.propTypes = {
  handleSubmit: PropTypes.func,
  pages: PropTypes.number,
  page: PropTypes.number,
  maxNumPageButtons: PropTypes.number
}

export {DataTablePagination}
